import React, { useEffect, useState } from "react"
import { Modal, Button } from "antd"
import { GoogleMaps } from "components"
import Strings from "utils/strings"
import { useSelector } from "react-redux"

export const ConfirmationMapModal: React.FC<{
	orders: any[]
	contract: any
	selectedOrders: string[]
	setShowConfirmModal: (show: boolean) => void
	onConfirm: () => void
}> = ({ orders, contract, selectedOrders, setShowConfirmModal, onConfirm }) => {
	const mobile = useSelector((state: any) => state.mobile)
	const [modalVisible, setModalVisible] = useState(true)

	const isProduction = process.env.API === "prod"
	const confirmButtonDelaySeconds = isProduction ? 3 : 1

	const [confirmButtonEnabled, setConfirmButtonEnabled] = useState(false)
	const [remainingTime, setRemainingTime] = useState(confirmButtonDelaySeconds)

	useEffect(() => {
		setConfirmButtonEnabled(false)
		setRemainingTime(confirmButtonDelaySeconds)

		const interval = setInterval(() => {
			setRemainingTime((prev) => {
				if (prev <= 1) {
					setConfirmButtonEnabled(true)
					clearInterval(interval)
					return 0
				}
				return prev - 1
			})
		}, 1000)

		return () => clearInterval(interval)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []) // Empty array - only run on mount

	const handleCancel = () => {
		setModalVisible(false)
		// Delay to allow modal animation to finish before unrendering
		setTimeout(() => {
			setShowConfirmModal(false)
		}, 300)
	}

	// Filter orders to only show selected ones
	const filteredOrders = orders.filter((order) =>
		selectedOrders.includes(order._id)
	)

	// Calculate markers based on orders
	const orderMarkers = filteredOrders
		.map((order) => {
			if (
				typeof order.formattedAddress?.location?.lat !== "number" ||
				typeof order.formattedAddress?.location?.lng !== "number"
			)
				return undefined

			const hasWarning =
				!!order?.orderWarnings &&
				Object.values(order.orderWarnings).some(Boolean)
			const hasError =
				!!order?.orderErrors && Object.values(order.orderErrors).some(Boolean)

			return {
				lat: order.formattedAddress.location.lat,
				lng: order.formattedAddress.location.lng,
				icon: {
					path: 0, // google.maps.SymbolPath.CIRCLE === 0
					fillColor: hasError ? "red" : hasWarning ? "orange" : "green",
					fillOpacity: 1,
					scale: 8,
					strokeColor: "black",
					strokeWeight: 1,
				},
				order: order,
			}
		})
		.filter((marker): marker is NonNullable<typeof marker> => !!marker)

	// Add depot marker if depot coordinates exist on contract
	const depotLat = contract?.depotCoordinates?.lat
	const depotLng = contract?.depotCoordinates?.lng
	const depotMarker =
		typeof depotLat === "number" && typeof depotLng === "number"
			? {
					lat: depotLat,
					lng: depotLng,
					icon: {
						path: 0, // google.maps.SymbolPath.CIRCLE === 0
						fillColor: "grey",
						fillOpacity: 1,
						scale: 8,
						strokeColor: "black",
						strokeWeight: 1,
					},
					overlay: (
						<div style={{ fontWeight: 600, fontSize: 14, color: "#666" }}>
							Depot
						</div>
					),
				}
			: null

	const markers = depotMarker ? [...orderMarkers, depotMarker] : orderMarkers

	// Calculate bounding box for all markers
	const boundingBox = markers.length
		? {
				south: Math.min(...markers.map((m) => m.lat)),
				west: Math.min(...markers.map((m) => m.lng)),
				north: Math.max(...markers.map((m) => m.lat)),
				east: Math.max(...markers.map((m) => m.lng)),
			}
		: undefined

	// Set working area polygon if contract is provided
	let workingAreaPolygons = null
	if (contract?.deliveryZones && contract.deliveryZones.length > 0) {
		const features = contract.deliveryZones.flatMap((zone: any) => {
			return zone.geojson.features ? zone.geojson.features : []
		})

		workingAreaPolygons = features
			.map((area: any) => {
				const geometry = area.geometry
				let polygons: { lat: number; lng: number }[][] = []

				if (geometry.type === "Polygon") {
					polygons = geometry.coordinates.map((ring: number[][]) =>
						ring.map((coord: number[]) => ({ lat: coord[1], lng: coord[0] }))
					)
				} else if (geometry.type === "MultiPolygon") {
					polygons = geometry.coordinates.flatMap((polygon: number[][][]) =>
						polygon.map((ring: number[][]) =>
							ring.map((coord: number[]) => ({ lat: coord[1], lng: coord[0] }))
						)
					)
				}

				return polygons.map((paths) => ({
					paths,
					fillColor: "#75e36d",
					fillOpacity: 0.2,
					strokeColor: "#75e36d",
					strokeOpacity: 0.8,
					strokeWeight: 3,
				}))
			})
			.flat()
	}

	const title = `${Strings.orders.definePicking} (${selectedOrders.length} ${Strings.orders.order}${selectedOrders.length !== 1 ? "s" : ""})`

	const footer = [
		<Button key="cancel" onClick={handleCancel}>
			{Strings.generic.cancel}
		</Button>,
		<Button
			key="confirm"
			type="primary"
			disabled={!confirmButtonEnabled}
			onClick={() => onConfirm?.()}
		>
			{confirmButtonEnabled
				? Strings.orders.definePicking
				: `${Strings.orders.definePicking} (${remainingTime}s)`}
		</Button>,
	]

	return (
		<Modal
			centered
			closable
			onCancel={handleCancel}
			visible={modalVisible}
			footer={footer}
			bodyStyle={{ padding: 0 }}
			width={mobile ? "100%" : 900}
		>
			<div className="UploadModal">
				<div className="UploadTitle">
					<p>{title}</p>
				</div>

				{markers.length > 0 ? (
					<GoogleMaps
						loadScript={false}
						overlay
						zoom={15}
						boundingBox={boundingBox}
						mapContainerStyle={{
							width: "100%",
							height: "60vh",
							borderRadius: 5,
						}}
						options={{ panControl: false }}
						markers={markers}
						polygons={workingAreaPolygons}
					/>
				) : (
					<div
						style={{
							height: "60vh",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							backgroundColor: "#f5f5f5",
							color: "#666",
						}}
					>
						<p>No orders with valid coordinates to display on map</p>
					</div>
				)}
			</div>
		</Modal>
	)
}
